/**
 * Client-side auth-error handling: detect "not authenticated / session expired"
 * responses and send the user to the login screen with a message, instead of
 * letting actions silently fail.
 */

import { stripPasswordQuery } from "@/lib/login-redirect";

let redirecting = false;
let refreshInFlight: Promise<boolean> | null = null;
let onSessionRecovered: (() => void) | null = null;

/** Materials pages call this after a silent refresh so the open screen reloads. */
export function setAuthRecoveryHandler(handler: (() => void) | null): void {
  onSessionRecovered = handler;
}

/** One refresh attempt. A 15-minute access cookie must not wipe a still-valid 7-day sign-in. */
export function refreshSessionOnce(): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (!refreshInFlight) {
    refreshInFlight = fetch("/api/auth/refresh", { method: "POST", credentials: "include" })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/** True when an error represents an expired / missing session (HTTP 401). */
export function isAuthError(err: unknown): boolean {
  if (err == null) return false;

  // ApiError (lib/api-client) and fetch wrappers expose a numeric status.
  const status = (err as { status?: unknown }).status;
  if (typeof status === "number" && status === 401) return true;

  const message =
    err instanceof Error ? err.message : typeof err === "string" ? err : "";
  return /(^401\b)|unauthorized|session expired|not authenticated/i.test(message);
}

/**
 * Redirect to the login page, preserving where the user was so they return
 * after signing in. Guards against redirect loops and double-fires.
 */
/** Magic-link / public fill pages must keep working when a background query 401s. */
export function isPublicMagicLinkPath(pathname: string): boolean {
  return (
    pathname.startsWith("/form/") ||
    pathname.startsWith("/survey/") ||
    pathname.startsWith("/r/") ||
    pathname.startsWith("/fg/") ||
    pathname.startsWith("/response/") ||
    pathname.startsWith("/join/") ||
    pathname.startsWith("/dow/") ||
    pathname.startsWith("/gantt/") ||
    pathname.startsWith("/cp/") ||
    pathname.startsWith("/model/")
  );
}

export function redirectToLogin(reason: string = "expired"): void {
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/login")) return;
  if (isPublicMagicLinkPath(window.location.pathname)) return;
  if (redirecting) return;
  redirecting = true;

  const params = new URLSearchParams();
  params.set("redirect", stripPasswordQuery(window.location.pathname + window.location.search));
  params.set("reason", reason);
  window.location.assign(`/login?${params.toString()}`);
}

/**
 * If `err` is an auth error, redirect to login and return true; otherwise return
 * false so callers can keep their own error handling.
 */
export function handleAuthError(err: unknown): boolean {
  if (!isAuthError(err)) return false;
  void refreshSessionOnce().then((recovered) => {
    if (recovered) {
      onSessionRecovered?.();
      return;
    }
    redirectToLogin();
  });
  return true;
}
